import { z } from "zod";
import {
  AuthUserSchema,
  AuthClientSchema,
  AuthOrganizationSchema,
  AuthExpertSchema,
  UserTypeSchema,
} from "./common";

// ==================
// Login Response
// ==================

/**
 * Response from POST /api/v2/auth/login
 * Minimal response - use auth check for full user details
 */
export const LoginResponseSchema = z.object({
  user: AuthUserSchema,
  type: UserTypeSchema,
  token: z.string(),
});

export type LoginResponse = z.infer<typeof LoginResponseSchema>;

// ==================
// Register Response
// ==================

/**
 * Response from POST /api/v2/auth/register
 * Minimal response - use auth check for full user details
 */
export const RegisterResponseSchema = z.object({
  user: AuthUserSchema,
  type: z.literal("client"),
  token: z.string(),
});

export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;

// ==================
// Auth Check Response
// ==================

/**
 * Base auth check response fields (common to both client and expert)
 */
const AuthCheckBaseSchema = z.object({
  user: AuthUserSchema,
  type: UserTypeSchema,
  is_validated: z.boolean(),
});

/**
 * Auth check response for clients
 */
export const AuthCheckClientResponseSchema = AuthCheckBaseSchema.extend({
  type: z.literal("client"),
  client: AuthClientSchema.optional(),
  organization: AuthOrganizationSchema.optional(),
});

export type AuthCheckClientResponse = z.infer<typeof AuthCheckClientResponseSchema>;

/**
 * Auth check response for experts
 */
export const AuthCheckExpertResponseSchema = AuthCheckBaseSchema.extend({
  type: z.literal("expert"),
  expert: AuthExpertSchema.optional(),
  todos: z.array(z.unknown()).optional(),
});

export type AuthCheckExpertResponse = z.infer<typeof AuthCheckExpertResponseSchema>;

/**
 * Combined auth check response (client or expert)
 */
export const AuthCheckResponseSchema = z.discriminatedUnion("type", [
  AuthCheckClientResponseSchema,
  AuthCheckExpertResponseSchema,
]);

export type AuthCheckResponse = z.infer<typeof AuthCheckResponseSchema>;

// ==================
// Check Email Response
// ==================

/**
 * Response from POST /api/v2/auth/check-email
 * Indicates whether an email exists and if it belongs to a client
 */
export const CheckEmailResponseSchema = z.object({
  exists: z.boolean(),
  has_client: z.boolean(),
});

export type CheckEmailResponse = z.infer<typeof CheckEmailResponseSchema>;

// ==================
// Success Message Response
// ==================

/**
 * Generic success response with message only.
 * Used for: forgot-password, reset-password, verify-email, send-verification
 */
export const AuthSuccessMessageResponseSchema = z.object({
  success: z.literal(true),
  message: z.string(),
});

export type AuthSuccessMessageResponse = z.infer<typeof AuthSuccessMessageResponseSchema>;

// ==================
// Convex JWT Response
// ==================

/**
 * Response payload from POST /api/v2/auth/jwt-token
 */
export const ConvexJwtTokenResponseSchema = z.object({
  application_id: z.string(),
  token_type: z.string(),
  expires_in: z.number(),
  token: z.string(),
});

export type ConvexJwtTokenResponse = z.infer<typeof ConvexJwtTokenResponseSchema>;

// ==================
// Error Response
// ==================

/**
 * Validation error response (422)
 */
export const AuthValidationErrorResponseSchema = z.object({
  success: z.literal(false),
  message: z.string(),
  errors: z.record(z.string(), z.array(z.string())).optional(),
});

export type AuthValidationErrorResponse = z.infer<typeof AuthValidationErrorResponseSchema>;

